/**
 * Switch-list request + track-inventory types (Plan 24).
 *
 * These mirror the backend records:
 *   - SwitchListRequestDTO / PlannedMoveDTO / CoupledCarDTO
 *   - TrackCarInventoryDTO / TrackCarDTO
 *   - WorkOrderResponseDTO (list row)
 */

/** PlannedMoveDTO — one cut move. `targetDoorNumber` is a request-side hint
 * resolved to `targetSpotId` server-side (AD-3); it is never persisted. */
export interface PlannedMove {
  moveId?: string
  moveKind: string
  sourceTrackId?: string
  targetTrackId: string
  targetSpotId?: string
  targetDoorNumber?: string
  carIds: string[]
  priority?: number
}

/** CoupledCarDTO — locomotive's current cut attributes. */
export interface CoupledCarRequest {
  railCarId?: string
  hazardous?: boolean
  hazmatClass?: string
  tareWeightTons?: number
  grossWeightTons?: number
}

/** SwitchListRequestDTO — POST /api/v1/switchlists/generate payload. */
export interface SwitchListGenerateRequest {
  workOrderId?: string
  facilityId: string
  locomotiveId: string
  /** Optional — server resolves explicit → latest track snap → rail car record → 400 (AD-9). */
  currentTrackId?: string
  couplingWorkOrder?: boolean
  plannedMoves: PlannedMove[]
  coupledCars?: CoupledCarRequest[]
}

/** TrackCarDTO — one car's ordered position on a track. */
export interface TrackCar {
  railCarId: string
  reportingMark?: string
  assetType?: string
  linearOffsetFeet?: number
  lengthFeet?: number
  loadStatus?: string
  hazardous?: boolean
  hazmatClass?: string
  /** Spot fields — populated only on SPUR / INDUSTRY_SIDING tracks. */
  spotId?: string
  spotIdentifier?: string
  doorNumber?: string
  dwellMinutes?: number
}

/** TrackCarInventoryDTO — GET /api/v1/yards/{facilityId}/tracks/{trackId}/cars. */
export interface TrackCarInventory {
  trackId: string
  trackName?: string
  trackType?: string
  /** Ordered A-end → B-end (linearOffset ascending). */
  cars: TrackCar[]
}

/** WorkOrderResponseDTO — row of GET /api/v1/workorders. */
export interface WorkOrderSummary {
  id: string
  woNumber?: string
  swlNumber?: string
  status?: string
  acceptedAt?: string
  completedAt?: string
  cancelledAt?: string
}

/** Non-terminal statuses (WorkOrder.OrderStatus) — used by the queue drawer. */
export const ACTIVE_WORK_ORDER_STATUSES = [
  'DISPATCHED',
  'IN_PROGRESS'
] as const

/** Terminal statuses — shown muted; sorted below active rows. */
export const TERMINAL_WORK_ORDER_STATUSES = [
  'COMPLETED',
  'CANCELLED',
  'EXPIRED_STALE',
  'STALE_CONFLICT'
] as const
